import { useState, useEffect } from "react";
import { FiClock } from "react-icons/fi";
import { checkMaintenanceMode } from "../../services/configService";

const MaintenanceBanner = () => {
  const [maintenanceInfo, setMaintenanceInfo] = useState({
    isMaintenanceMode: false,
    message: null,
    expectedResumeTime: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkMaintenance = async () => {
      const result = await checkMaintenanceMode();
      setMaintenanceInfo(result);
      setLoading(false);
    };

    checkMaintenance();

    // Poll every 60 seconds to check for maintenance status changes
    const interval = setInterval(checkMaintenance, 60000);
    return () => clearInterval(interval);
  }, []);

  // Format the expected resume time
  const formatResumeTime = (isoString) => {
    if (!isoString) {
      return null;
    }
    try {
      const date = new Date(isoString);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZoneName: "short",
      });
    } catch {
      return null;
    }
  };

  // Don't render if not in maintenance mode or still loading
  if (loading || !maintenanceInfo.isMaintenanceMode) {
    return null;
  }

  const formattedResumeTime = formatResumeTime(
    maintenanceInfo.expectedResumeTime
  );

  return (
    <div className="relative overflow-hidden bg-gradient-to-t from-amber-500 to-amber-400">
      {/* Subtle diagonal stripes pattern */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: `repeating-linear-gradient(
            -45deg,
            #000 0px,
            #000 10px,
            transparent 10px,
            transparent 20px
          )`,
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 py-2.5 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
          {/* Status indicator with pulse */}
          <span className="flex items-center gap-2">
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-gray-900 opacity-50" />
              <span className="relative inline-flex size-2 rounded-full bg-gray-900" />
            </span>
            <span className="text-sm font-semibold text-gray-900">
              Site under Maintenance
            </span>
          </span>

          <span className="hidden text-gray-900/30 sm:inline">•</span>

          <span className="text-sm text-gray-800">
            Benchmark Runs are Paused
          </span>

          {formattedResumeTime && (
            <>
              <span className="hidden text-gray-900/30 sm:inline">•</span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-900/20 bg-gray-900/10 px-3 py-0.5 text-xs font-medium text-gray-900">
                <FiClock className="size-3.5" />
                Estimated Resume: {formattedResumeTime}
              </span>
            </>
          )}

          {maintenanceInfo.message && (
            <>
              <span className="hidden text-gray-900/30 sm:inline">•</span>
              <span className="text-sm text-gray-800">
                {maintenanceInfo.message}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceBanner;
